import { useCallback } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { ExpenseUseCaseErrors } from '@webapp/modules/expense/business-logic/use-cases/ExpenseUseCaseErrors'
import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/CreateExpenseReportForm'
import messages from './messages'
import { useMutateSubmitExpenseReport } from '../../models/useMutateSubmitExpenseReport'

interface UseSubmitExpenseReportPresenterParams {
  onSuccess: (selfApproved: boolean) => void
  onBudgetError: (formValues: ExpenseReportFormSchema) => void
}

export const useSubmitExpenseReportPresenter = ({
  onSuccess,
  onBudgetError,
}: UseSubmitExpenseReportPresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateSubmitExpenseReport()

  const submitReport = useCallback(
    async (
      formValues: ExpenseReportFormSchema,
      shouldSkipBudgetCheck: boolean
    ) => {
      try {
        const result = await mutateAsync({ formValues, shouldSkipBudgetCheck })
        onSuccess(result.selfApproved)
      } catch (err) {
        if (err instanceof ExpenseUseCaseErrors) {
          if (err.name === 'EXPENSE_SUBMIT_OVER_BUDGET_CONFIRMATION_REQUIRED') {
            onBudgetError(formValues)
            return
          }
          if (err.name === 'EXPENSE_SUBMIT_OVER_BUDGET') {
            toast.error({
              title: intl.formatMessage(messages.budgetExceededErrorTitle),
              children: intl.formatMessage(messages.budgetExceededError),
            })
            return
          }
        }

        const errorMessage =
          getMessageFromError(err) ?? intl.formatMessage(messages.genericError)

        toast.error({
          title: intl.formatMessage(messages.genericErrorTitle),
          children: errorMessage,
        })
      }
    },
    [intl, mutateAsync, onBudgetError, onSuccess]
  )

  return {
    commands: {
      submitReport,
    },
    states: {
      isLoading,
    },
  }
}
